/**
 * Song Metadata Validation Utilities
 *
 * Implements comprehensive validation patterns inspired by Stars Layer's
 * StarVariantValidator for robust song metadata validation.
 * Aligned with OpenAPI SongMetadataRequest: required fields, maxLength 255, genre/mood arrays 1-50.
 */

import {
  SONG_METADATA,
  validateStringMaxLength,
  validateArrayLength,
  validateArrayItems,
} from '../constants/apiMetadataConstraints';

export interface SongValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  fieldErrors: Record<string, string[]>;
}

export interface SongMetadata {
  // Core song information
  songName?: string;
  artistName?: string;
  albumName?: string;
  genre?: string | string[];
  mood?: string | string[];
  energy?: string;
  tempo?: string;
  bpm?: number;
  duration?: number;
  key?: string;
  vocalType?: string;
  ageAppropriateness?: string[];
  releaseYear?: number;
  originalArtistGender?: string;
  genderSuitability?: string;

  // Base/Variant architecture (Universal NNA Framework pattern)
  assetType?: 'base' | 'variant';
  baseSongId?: string; // HFN of base song for variants
  variantType?:
    | 'cover'
    | 'remix'
    | 'acoustic'
    | 'instrumental'
    | 'mashup'
    | 'karaoke';
  variantName?: string;
  variantDescription?: string;
}

export class SongMetadataValidator {
  /**
   * Validates complete song metadata
   */
  static validateSongMetadata(
    songMetadata: SongMetadata | undefined
  ): SongValidationResult {
    const result: SongValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      fieldErrors: {},
    };

    if (!songMetadata) {
      result.errors.push('Song metadata is required for Songs layer assets');
      result.isValid = false;
      return result;
    }

    // Core validations
    this.validateCoreFields(songMetadata, result);
    this.validateMusicalFields(songMetadata, result);
    this.validateMetadataFields(songMetadata, result);
    this.validateCompatibilityFields(songMetadata, result);

    // Base/Variant architecture validation (Universal NNA Framework pattern)
    this.validateBaseVariantArchitecture(songMetadata, result);

    return result;
  }

  /**
   * Validates core song information
   */
  private static validateCoreFields(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { songName, artistName, albumName } = songMetadata;

    // Song name validation (OpenAPI: required, maxLength 255)
    if (!songName || songName.trim() === '') {
      this.addFieldError(result, 'songName', 'Song name is required');
    } else {
      const msg = validateStringMaxLength(songName, SONG_METADATA.stringMaxLength, 'Song name');
      if (msg) this.addFieldError(result, 'songName', msg);
    }

    // Artist name validation (OpenAPI: required, maxLength 255)
    if (!artistName || artistName.trim() === '') {
      this.addFieldError(result, 'artistName', 'Artist name is required');
    } else {
      const msg = validateStringMaxLength(artistName, SONG_METADATA.stringMaxLength, 'Artist name');
      if (msg) this.addFieldError(result, 'artistName', msg);
    }

    // Album name validation (optional; OpenAPI maxLength 255)
    if (albumName && albumName.trim() !== '') {
      const msg = validateStringMaxLength(albumName, SONG_METADATA.stringMaxLength, 'Album name');
      if (msg) this.addFieldError(result, 'albumName', msg);
    }
  }

  /**
   * Validates musical technical fields
   */
  private static validateMusicalFields(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { bpm, duration, key, tempo, energy } = songMetadata;

    // Tempo validation (OpenAPI SongMetadataRequest: required, maxLength 255)
    if (!tempo || (typeof tempo === 'string' && tempo.trim() === '')) {
      this.addFieldError(result, 'tempo', 'Tempo is required');
    } else if (typeof tempo === 'string') {
      const msg = validateStringMaxLength(tempo, SONG_METADATA.stringMaxLength, 'Tempo');
      if (msg) this.addFieldError(result, 'tempo', msg);
    }

    // BPM validation
    if (bpm !== null && bpm !== undefined) {
      if (typeof bpm !== 'number' || isNaN(bpm)) {
        this.addFieldError(result, 'bpm', 'BPM must be a valid number');
      } else if (bpm < 40 || bpm > 200) {
        this.addFieldError(result, 'bpm', 'BPM must be between 40 and 200');
      } else if (bpm < 60) {
        this.addFieldWarning(
          result,
          'bpm',
          'Very slow BPM (under 60) - please verify'
        );
      } else if (bpm > 180) {
        this.addFieldWarning(
          result,
          'bpm',
          'Very fast BPM (over 180) - please verify'
        );
      }
    }

    // Duration validation
    if (duration !== null && duration !== undefined) {
      if (typeof duration !== 'number' || isNaN(duration)) {
        this.addFieldError(
          result,
          'duration',
          'Duration must be a valid number (in seconds)'
        );
      } else if (duration < 15 || duration > 1800) {
        this.addFieldError(
          result,
          'duration',
          'Duration must be between 15 seconds and 30 minutes'
        );
      } else if (duration < 30) {
        this.addFieldWarning(
          result,
          'duration',
          'Very short duration (under 30 seconds) - please verify'
        );
      } else if (duration > 600) {
        this.addFieldWarning(
          result,
          'duration',
          'Very long duration (over 10 minutes) - please verify'
        );
      }
    }

    // Musical key validation
    if (key && key.trim() !== '') {
      if (!this.isValidKeyFormat(key)) {
        this.addFieldError(
          result,
          'key',
          'Key format should be like "C major", "F# minor", "Bb major"'
        );
      }
    }

    // Cross-validation: BPM vs Tempo consistency
    if (bpm && tempo) {
      const tempoFromBPM = this.inferTempoFromBPM(bpm);
      if (tempoFromBPM && tempo.toLowerCase() !== tempoFromBPM.toLowerCase()) {
        this.addFieldWarning(
          result,
          'tempo',
          `Tempo "${tempo}" may not match BPM ${bpm} (suggests "${tempoFromBPM}")`
        );
      }
    }

    // Cross-validation: Energy vs BPM consistency
    if (bpm && energy) {
      const energyFromBPM = this.inferEnergyFromBPM(bpm);
      if (
        energyFromBPM &&
        energy.toLowerCase() !== energyFromBPM.toLowerCase()
      ) {
        this.addFieldWarning(
          result,
          'energy',
          `Energy "${energy}" may not match BPM ${bpm} (suggests "${energyFromBPM}")`
        );
      }
    }
  }

  /**
   * Validates metadata and classification fields
   */
  private static validateMetadataFields(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { genre, mood, releaseYear } = songMetadata;

    // Genre validation (OpenAPI: required, array min 1 max 50, items maxLength 255)
    const genreArr = Array.isArray(genre) ? genre : genre != null && typeof genre === 'string' ? [genre] : [];
    if (genreArr.length === 0) {
      this.addFieldError(result, 'genre', 'Genre is required (at least one)');
    } else {
      let msg = validateArrayLength(genreArr, SONG_METADATA.genre, 'Genre');
      if (msg) this.addFieldError(result, 'genre', msg);
      else {
        msg = validateArrayItems(genreArr, SONG_METADATA.stringMaxLength, 'Genre');
        if (msg) this.addFieldError(result, 'genre', msg);
      }
    }

    // Mood validation (OpenAPI: required, array min 1 max 50, items maxLength 255)
    const moodArr = Array.isArray(mood) ? mood : mood != null && typeof mood === 'string' ? [mood] : [];
    if (moodArr.length === 0) {
      this.addFieldError(result, 'mood', 'Mood is required (at least one)');
    } else {
      let msg = validateArrayLength(moodArr, SONG_METADATA.mood, 'Mood');
      if (msg) this.addFieldError(result, 'mood', msg);
      else {
        msg = validateArrayItems(moodArr, SONG_METADATA.stringMaxLength, 'Mood');
        if (msg) this.addFieldError(result, 'mood', msg);
      }
    }

    // Release year validation
    if (releaseYear !== null && releaseYear !== undefined) {
      const currentYear = new Date().getFullYear();
      if (typeof releaseYear !== 'number' || isNaN(releaseYear)) {
        this.addFieldError(
          result,
          'releaseYear',
          'Release year must be a valid number'
        );
      } else if (releaseYear < 1900 || releaseYear > currentYear + 2) {
        this.addFieldError(
          result,
          'releaseYear',
          `Release year must be between 1900 and ${currentYear + 2}`
        );
      }
    }

    // ageAppropriateness not in SongMetadataRequest; only in algorhythmMetadata - no validation here
  }

  /**
   * Validates compatibility and context fields
   */
  private static validateCompatibilityFields(
    _songMetadata: SongMetadata,
    _result: SongValidationResult
  ): void {
    // originalArtistGender, genderSuitability, vocalType not in SongMetadataRequest - no validation
  }

  /**
   * Validates base/variant architecture (Universal NNA Framework pattern)
   * Follows same patterns as StarVariantValidator for architectural consistency
   */
  private static validateBaseVariantArchitecture(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { assetType } = songMetadata;

    // Default to 'base' if not specified
    const effectiveAssetType = assetType || 'base';

    // Validate based on asset type
    if (effectiveAssetType === 'base') {
      this.validateBaseSong(songMetadata, result);
    } else if (effectiveAssetType === 'variant') {
      this.validateVariantSong(songMetadata, result);
    } else {
      this.addFieldError(
        result,
        'assetType',
        'Asset type must be either "base" or "variant"'
      );
    }
  }

  /**
   * Validates base song rules
   */
  private static validateBaseSong(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { baseSongId } = songMetadata;

    // For base songs: baseSongId should be empty
    if (baseSongId && baseSongId.trim() !== '') {
      this.addFieldError(
        result,
        'baseSongId',
        'Base songs cannot reference other songs. Base Song ID should be empty for original songs.'
      );
    }

    // Base songs should have comprehensive metadata
    if (!songMetadata.songName || !songMetadata.artistName) {
      this.addFieldWarning(
        result,
        'songName',
        'Base songs should have complete song and artist information'
      );
    }
  }

  /**
   * Validates variant song rules (covers, remixes, etc.)
   */
  private static validateVariantSong(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { baseSongId, variantType, variantName } = songMetadata;

    // baseSongId is required for variants
    if (!baseSongId || baseSongId.trim() === '') {
      this.addFieldError(
        result,
        'baseSongId',
        'Original Song ID is required for variant songs'
      );
    } else {
      // Validate HFN format (Songs layer: G.XXX.XXX.XXX)
      if (!this.isValidSongHFNFormat(baseSongId)) {
        this.addFieldError(
          result,
          'baseSongId',
          'Original Song ID must be a valid Songs layer HFN format (e.g., G.POP.CON.001)'
        );
      }
    }

    // variantType is required for variants
    if (!variantType || variantType.trim() === '') {
      this.addFieldError(
        result,
        'variantType',
        'Variant type is required for variant songs'
      );
    } else {
      const validVariantTypes = [
        'cover',
        'remix',
        'acoustic',
        'instrumental',
        'mashup',
        'karaoke',
      ];
      if (!validVariantTypes.includes(variantType.toLowerCase())) {
        this.addFieldError(
          result,
          'variantType',
          `Invalid variant type: ${variantType}. Valid options: ${validVariantTypes.join(
            ', '
          )}`
        );
      }
    }

    // variantName is required for variants
    if (!variantName || variantName.trim() === '') {
      this.addFieldError(
        result,
        'variantName',
        'Variant name is required for variant songs'
      );
    } else if (variantName.length > 100) {
      this.addFieldWarning(
        result,
        'variantName',
        'Variant name is quite long (over 100 characters)'
      );
    }

    // Variant-specific validation based on type
    this.validateVariantTypeSpecifics(songMetadata, result);
  }

  /**
   * Validates variant type-specific requirements
   */
  private static validateVariantTypeSpecifics(
    songMetadata: SongMetadata,
    result: SongValidationResult
  ): void {
    const { variantType, artistName } = songMetadata;

    if (!variantType) return;

    switch (variantType.toLowerCase()) {
      case 'cover':
        // Covers should have different artist but same song name
        if (!artistName || artistName.trim() === '') {
          this.addFieldWarning(
            result,
            'artistName',
            'Cover versions should specify the covering artist'
          );
        }
        break;

      case 'remix':
        // Remixes might have different BPM/tempo
        if (songMetadata.bpm) {
          this.addFieldWarning(
            result,
            'bpm',
            'Verify BPM is appropriate for this remix version'
          );
        }
        break;

      case 'acoustic':
        // Acoustic versions typically have lower energy/BPM
        if (songMetadata.energy === 'High') {
          this.addFieldWarning(
            result,
            'energy',
            'Acoustic versions typically have lower energy levels'
          );
        }
        break;

      case 'instrumental':
        break;

      case 'karaoke':
        break;
    }
  }

  /**
   * Validates Songs layer HFN format (G.XXX.XXX.XXX)
   */
  private static isValidSongHFNFormat(hfn: string): boolean {
    // Songs layer pattern: G.XXX.XXX.XXX
    const hfnPattern = /^G\.[A-Z]{3}\.[A-Z]{3}\.\d{3}$/;
    return hfnPattern.test(hfn);
  }

  /**
   * Helper: Add field-specific error
   */
  private static addFieldError(
    result: SongValidationResult,
    field: string,
    message: string
  ): void {
    result.isValid = false;
    result.errors.push(message);

    if (!result.fieldErrors[field]) {
      result.fieldErrors[field] = [];
    }
    result.fieldErrors[field].push(message);
  }

  /**
   * Helper: Add field-specific warning
   */
  private static addFieldWarning(
    result: SongValidationResult,
    field: string,
    message: string
  ): void {
    result.warnings.push(message);

    if (!result.fieldErrors[field]) {
      result.fieldErrors[field] = [];
    }
    result.fieldErrors[field].push(message);
  }

  /**
   * Validates musical key format
   */
  private static isValidKeyFormat(key: string): boolean {
    const keyPattern = /^[A-G][#b]?\s+(major|minor)$/i;
    return keyPattern.test(key.trim());
  }

  /**
   * Infers tempo from BPM for consistency checking
   */
  private static inferTempoFromBPM(bpm: number): string | null {
    if (bpm < 80) return 'Slow';
    if (bpm < 120) return 'Medium';
    if (bpm < 160) return 'Fast';
    return 'Fast';
  }

  /**
   * Infers energy from BPM for consistency checking
   */
  private static inferEnergyFromBPM(bpm: number): string | null {
    if (bpm < 90) return 'Low';
    if (bpm < 130) return 'Medium';
    return 'High';
  }

  /**
   * Generates user-friendly validation messages
   */
  static getValidationSummary(result: SongValidationResult): {
    hasIssues: boolean;
    summary: string;
    recommendations: string[];
  } {
    const hasIssues = !result.isValid || result.warnings.length > 0;

    let summary = '';
    if (!result.isValid) {
      summary = `${result.errors.length} error${
        result.errors.length > 1 ? 's' : ''
      } found`;
    } else if (result.warnings.length > 0) {
      summary = `${result.warnings.length} warning${
        result.warnings.length > 1 ? 's' : ''
      } found`;
    } else {
      summary = 'All validations passed';
    }

    const recommendations: string[] = [];

    // Add specific recommendations based on common issues
    if (result.fieldErrors.genre) {
      recommendations.push(
        'Consider adding a genre for better discoverability'
      );
    }
    if (result.fieldErrors.mood) {
      recommendations.push('Adding mood helps with playlist curation');
    }
    if (result.fieldErrors.bpm && result.fieldErrors.tempo) {
      recommendations.push('Ensure BPM and tempo descriptions are consistent');
    }

    return {
      hasIssues,
      summary,
      recommendations,
    };
  }

  /**
   * Quick validation for form fields (lightweight)
   */
  static validateField(
    fieldName: keyof SongMetadata,
    value: any,
    context?: SongMetadata
  ): { isValid: boolean; message?: string } {
    switch (fieldName) {
      case 'songName':
        if (!value || value.trim() === '') {
          return { isValid: false, message: 'Song name is required' };
        }
        break;

      case 'artistName':
        if (!value || value.trim() === '') {
          return { isValid: false, message: 'Artist name is required' };
        }
        break;

      case 'bpm':
        if (value && (typeof value !== 'number' || value < 40 || value > 200)) {
          return { isValid: false, message: 'BPM must be between 40 and 200' };
        }
        break;

      case 'duration':
        if (
          value &&
          (typeof value !== 'number' || value < 15 || value > 1800)
        ) {
          return {
            isValid: false,
            message: 'Duration must be between 15 seconds and 30 minutes',
          };
        }
        break;

      case 'key':
        if (value && !this.isValidKeyFormat(value)) {
          return {
            isValid: false,
            message: 'Key format should be like "C major" or "F# minor"',
          };
        }
        break;
    }

    return { isValid: true };
  }

  /**
   * Generates helpful validation messages for UI (following StarVariantValidator pattern)
   */
  static getValidationMessages(
    songMetadata: SongMetadata | undefined,
    currentAssetHFN?: string
  ): {
    assetTypeHelp: string;
    baseSongIdHelp: string;
    variantNameHelp: string;
    variantTypeHelp: string;
  } {
    const effectiveAssetType = songMetadata?.assetType || 'base';

    return {
      assetTypeHelp:
        effectiveAssetType === 'base'
          ? 'This will be an original song that can have future variants (covers, remixes, etc.)'
          : 'This will be a variant of an existing original song',

      baseSongIdHelp:
        effectiveAssetType === 'base'
          ? `Will be set to this asset's HFN after registration: ${
              currentAssetHFN || 'To be generated'
            }`
          : songMetadata?.baseSongId
          ? `This will be a variant of ${songMetadata.baseSongId}`
          : 'Please enter the HFN address of the original song (e.g., G.POP.CON.001)',

      variantNameHelp:
        effectiveAssetType === 'variant'
          ? 'Required: Descriptive name for this variant (e.g., "Acoustic Version", "Club Remix")'
          : 'Not required for original songs',

      variantTypeHelp:
        effectiveAssetType === 'variant'
          ? this.getVariantTypeDescription(songMetadata?.variantType)
          : 'Not applicable for original songs',
    };
  }

  /**
   * Provides descriptions for variant types
   */
  private static getVariantTypeDescription(variantType?: string): string {
    const descriptions = {
      cover: 'Different artist performing the same song',
      remix: 'New arrangement or production of the original',
      acoustic: 'Stripped-down, unplugged version',
      instrumental: 'Version without vocals for karaoke or background use',
      mashup: 'Combination with other songs or elements',
      karaoke: 'Backing track for singing along',
    };

    return variantType
      ? descriptions[variantType as keyof typeof descriptions] ||
          'Custom variant type'
      : 'Select the type of variant you are creating';
  }

  /**
   * Validates if a string could be a Songs layer HFN (less strict for user input)
   */
  static couldBeSongHFN(input: string): boolean {
    if (!input || input.trim() === '') return false;

    // More lenient pattern for user input validation
    const relaxedPattern = /^G\.[A-Z]{2,3}\.[A-Z]{2,3}\.\d{1,3}$/i;
    return relaxedPattern.test(input.trim());
  }

  /**
   * Gets available variant types with descriptions for UI
   */
  static getAvailableVariantTypes(): Array<{
    value: string;
    label: string;
    description: string;
  }> {
    return [
      {
        value: 'cover',
        label: 'Cover Version',
        description: 'Different artist performing the same song',
      },
      {
        value: 'remix',
        label: 'Remix',
        description: 'New arrangement or production of the original',
      },
      {
        value: 'acoustic',
        label: 'Acoustic Version',
        description: 'Stripped-down, unplugged version',
      },
      {
        value: 'instrumental',
        label: 'Instrumental',
        description: 'Version without vocals',
      },
      {
        value: 'mashup',
        label: 'Mashup',
        description: 'Combination with other songs or elements',
      },
      {
        value: 'karaoke',
        label: 'Karaoke Version',
        description: 'Backing track for singing along',
      },
    ];
  }
}

export default SongMetadataValidator;
